import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaymentOption, Product, ProductType } from "@/core/entities/product";
import { useUserStore } from "@/stores/user";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { Edit, Eye, MoreHorizontal, Settings2, Trash2 } from "lucide-react";
import { useState } from "react";

interface PriceTier {
  minRange: number;
  maxRange: number;
  price: number;
}

interface ProductsTableProps {
  products: Product[];
  onView: (product: Product) => void;
  onEdit: (product: Product) => void;
  onDelete: (productId: string) => void;
}

export function ProductsTable({
  products,
  onView,
  onEdit,
  onDelete,
}: Readonly<ProductsTableProps>) {
  // const [auditLogOpen, setAuditLogOpen] = useState(false);
  // const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    logo: true,
    name: true,
    status: true,
    type: true,
    paymentOption: true,
    statusTiers: true,
    priceRange: true,
    updatedAt: true,
    actions: true,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const { hasFullAccess } = useUserStore();

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (productToDelete) {
      onDelete(productToDelete.id);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const getBuyOrSellBadge = (buyOrSell: ProductType) => {
    const variants = {
      buy: "bg-secondary text-secondary-foreground hover:bg-secondary",
      sell: "bg-secondary text-secondary-foreground hover:bg-muted",
      both: "bg-primary/10 text-primary hover:bg-primary/10",
    };
    return <Badge className={variants[buyOrSell]}>{buyOrSell}</Badge>;
  };

  const formatPriceRange = (prices: PriceTier[] | undefined) => {
    if (!prices || prices.length === 0) return "N/A";
    const minPrice = Math.min(...prices.map((p) => p.price));
    const maxPrice = Math.max(...prices.map((p) => p.price));
    return `$${minPrice} - $${maxPrice}`;
  };

  const formatPaymentOption = (option: PaymentOption | undefined) => {
    if (!option) return "-";
    const labels: Record<PaymentOption, string> = {
      [PaymentOption.PAY_IN_ADVANCE]: "Pay in Advance",
      [PaymentOption.PAY_ONCE_BOOKED]: "Pay Once Booked",
      [PaymentOption.PAY_AFTER_BOOKING]: "Pay After Booking",
    };
    return labels[option];
  };

  const columns: ColumnDef<Product>[] = [
    {
      id: "logo",
      accessorKey: "logo",
      header: "Product Logo",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center justify-center">
            {product.logo ? (
              <img
                src={product.logo}
                alt={product.name}
                className="h-18 rounded-md"
              />
            ) : (
              <span className="text-sm text-muted-foreground">
                No logo added
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Product Name",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div>
            <div className="font-semibold">{product.name}</div>
            <div className="text-sm text-muted-foreground">
              {product.category}
            </div>
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "inactive",
      header: "Status",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium ${
              product.inactive
                ? "bg-muted text-muted-foreground"
                : "bg-primary/10 text-primary hover:bg-primary/10"
            }`}
          >
            {product.inactive ? "Inactive" : "Active"}
          </span>
        );
      },
    },
    {
      id: "type",
      accessorKey: "buyOrSell",
      header: "Type",
      cell: ({ row }) => {
        return getBuyOrSellBadge(row.original.buyOrSell);
      },
    },
    {
      id: "paymentOption",
      accessorKey: "paymentOption",
      header: "Payment Option",
      cell: ({ row }) => {
        return (
          <span className="text-sm">
            {formatPaymentOption(row.original.paymentOption as PaymentOption)}
          </span>
        );
      },
    },
    {
      id: "statusTiers",
      accessorKey: "statusTiers",
      header: "Status Tiers",
      cell: ({ row }) => {
        const product = row.original;
        if (!product.statusTiers || product.statusTiers.length === 0) {
          return <span className="text-sm text-muted-foreground">None</span>;
        }
        return (
          <div className="text-sm">
            {product.statusTiers.slice(0, 2).map((tier, index) => (
              <div key={index} className="flex items-center">
                <span className="font-medium">{tier.name}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: "priceRange",
      accessorKey: "sellPrices",
      header: "Price Range",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="text-sm">
            {product.buyOrSell === ProductType.SELL ||
            product.buyOrSell === ProductType.BOTH ? (
              <div>Sell: {formatPriceRange(product.sellPrices)}</div>
            ) : null}
            {product.buyOrSell === ProductType.BUY ||
            product.buyOrSell === ProductType.BOTH ? (
              <div>Buy: {formatPriceRange(product.buyPrices)}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "updatedAt",
      accessorKey: "updatedAt",
      header: "Last Updated",
      cell: ({ row }) => {
        return new Date(row.original.updatedAt).toLocaleDateString();
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onView(product)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
                {/* <DropdownMenuItem onClick={() => {
                  setSelectedProduct(product);
                  setAuditLogOpen(true);
                }}>
                  <History className="mr-2 h-4 w-4" />
                  Audit Log
                </DropdownMenuItem> */}
                {hasFullAccess() && (
                  <>
                    <DropdownMenuItem onClick={() => onEdit(product)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Product
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDeleteClick(product)}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
  });

  return (
    <>
      <div className="flex flex-col gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto py-2">
              <Settings2 className="h-4 w-4" />
              View
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="rounded-md border overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={`relative group whitespace-nowrap ${header.column.id === "actions" ? "text-center w-12" : "text-left px-2"} border-r last:border-r-0`}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center ${header.column.id === "actions" ? "justify-center" : "justify-start"}`}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-muted/50 border-b"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`whitespace-nowrap ${cell.column.id === "actions" ? "text-center w-12" : "text-left"} border-r last:border-r-0`}
                      >
                        {cell.column.id === "actions" ? (
                          <div className="flex items-center justify-center">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{productToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* <AuditLogDialog
        open={auditLogOpen}
        onOpenChange={setAuditLogOpen}
        documentId={selectedProduct?.id || ""}
        documentName={selectedProduct?.name}
      /> */}
    </>
  );
}
